import { Injectable, inject, NgZone } from "@angular/core"
import { Router } from "@angular/router"
import { BehaviorSubject, Observable } from "rxjs"
import {
  NotificationConfig,
  NotificationPermissionState,
} from "../models/notification.model"

/**
 * Service for managing browser notifications and audio chimes
 * Provides reliable notifications that work even when the app is in the background
 */
@Injectable({
  providedIn: "root",
})
export class NotificationService {
  private router = inject(Router)
  private ngZone = inject(NgZone)

  // Permission state
  private permissionState$ = new BehaviorSubject<NotificationPermissionState>(
    this.getCurrentPermission()
  )

  // Audio context for chime (reused to avoid creation issues)
  private audioContext: AudioContext | null = null

  // Track active notifications
  private activeNotifications = new Map<string, Notification>()

  /**
   * Observable of current permission state
   */
  get permission$(): Observable<NotificationPermissionState> {
    return this.permissionState$.asObservable()
  }

  /**
   * Current permission state
   */
  get currentPermission(): NotificationPermissionState {
    return this.permissionState$.value
  }

  /**
   * Check if notifications are supported
   */
  get isSupported(): boolean {
    return "Notification" in window && typeof window.Notification !== "undefined"
  }

  /**
   * Check if notifications are available (supported + permitted)
   */
  get isAvailable(): boolean {
    return this.isSupported && this.currentPermission === "granted"
  }

  /**
   * Check if page is served over HTTPS (required for notifications)
   */
  get isSecureContext(): boolean {
    return window.isSecureContext || location.protocol === "https:"
  }

  /**
   * Get current notification permission from browser
   */
  private getCurrentPermission(): NotificationPermissionState {
    if (!this.isSupported) {
      return "denied"
    }
    return Notification.permission as NotificationPermissionState
  }

  /**
   * Request notification permission from user
   * @returns Promise resolving to the permission state
   */
  async requestPermission(): Promise<NotificationPermissionState> {
    if (!this.isSupported) {
      console.warn("Notifications not supported in this browser")
      return "denied"
    }

    // Already granted
    if (this.currentPermission === "granted") {
      return "granted"
    }

    // Already denied - can't request again
    if (this.currentPermission === "denied") {
      console.warn("Notification permission was previously denied")
      return "denied"
    }

    try {
      const permission = await Notification.requestPermission()
      const state = permission as NotificationPermissionState
      this.permissionState$.next(state)
      return state
    } catch (error) {
      console.error("Error requesting notification permission:", error)
      return "denied"
    }
  }

  /**
   * Show a browser notification
   * @param config Notification configuration
   * @returns The notification instance or null if failed
   */
  async show(config: NotificationConfig): Promise<Notification | null> {
    // Check permission
    if (!this.isAvailable) {
      console.warn(
        "Notifications not available. Permission:",
        this.currentPermission
      )
      return null
    }

    try {
      const notification = new Notification(config.title, {
        body: config.body,
        icon: config.icon || "/assets/icons/icon-192x192.png",
        badge: config.badge || "/assets/icons/badge-72x72.png",
        tag: config.tag,
        data: config.data,
        requireInteraction: config.requireInteraction ?? false,
        silent: config.silent ?? false,
      })

      // Track notification
      const notificationId = config.tag || `notification-${Date.now()}`
      this.activeNotifications.set(notificationId, notification)

      // Set up event handlers
      this.setupNotificationHandlers(notification, notificationId, config)

      // Auto-close if configured
      if (config.autoCloseMs && config.autoCloseMs > 0) {
        setTimeout(() => {
          notification.close()
        }, config.autoCloseMs)
      }

      return notification
    } catch (error) {
      console.error("Error creating notification:", error)
      return null
    }
  }

  /**
   * Set up event handlers for a notification
   */
  private setupNotificationHandlers(
    notification: Notification,
    notificationId: string,
    config: NotificationConfig
  ): void {
    notification.onclick = (event) => {
      event.preventDefault()

      // Focus the window
      window.focus()

      // Navigate if chartId is in data
      if (config.data?.["chartId"]) {
        this.ngZone.run(() => {
          this.router.navigate(["/charts", config.data?.["chartId"]])
        })
      }

      // Close notification
      notification.close()
    }

    notification.onclose = () => {
      this.activeNotifications.delete(notificationId)
    }

    notification.onerror = (error) => {
      console.error("Notification error:", error)
      this.activeNotifications.delete(notificationId)
    }
  }

  /**
   * Close a notification by tag
   */
  close(tag: string): void {
    const notification = this.activeNotifications.get(tag)
    if (notification) {
      notification.close()
      this.activeNotifications.delete(tag)
    }
  }

  /**
   * Close all active notifications
   */
  closeAll(): void {
    this.activeNotifications.forEach((notification) => {
      notification.close()
    })
    this.activeNotifications.clear()
  }

  /**
   * Play notification chime sound
   * Uses a persistent AudioContext to work better in background
   * @param volume Volume level (0.0 - 1.0)
   */
  async playChime(volume = 0.3): Promise<void> {
    try {
      // Initialize or resume audio context
      if (!this.audioContext) {
        const AudioContextClass =
          window.AudioContext ||
          (window as typeof window & { webkitAudioContext: typeof AudioContext })
            .webkitAudioContext
        this.audioContext = new AudioContextClass()
      }

      // Resume if suspended (required after user interaction)
      if (this.audioContext.state === "suspended") {
        await this.audioContext.resume()
      }

      const oscillator = this.audioContext.createOscillator()
      const gainNode = this.audioContext.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(this.audioContext.destination)

      // Create a pleasant two-tone chime
      const now = this.audioContext.currentTime
      oscillator.frequency.setValueAtTime(800, now)
      oscillator.frequency.setValueAtTime(1000, now + 0.1)

      // Volume envelope
      gainNode.gain.setValueAtTime(volume, now)
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.3)

      oscillator.start(now)
      oscillator.stop(now + 0.3)

      // Cleanup
      oscillator.onended = () => {
        oscillator.disconnect()
        gainNode.disconnect()
      }
    } catch (error) {
      console.error("Error playing chime:", error)
    }
  }

  /**
   * Show notification for new "In Review" chart
   * @param chartId Chart ID
   * @param chartTitle Chart title
   * @param playSound Whether to play audio chime
   */
  async notifyNewInReviewChart(
    chartId: string,
    chartTitle: string,
    playSound = true
  ): Promise<void> {
    // Show browser notification
    await this.show({
      title: "Chart Ready for Review",
      body: chartTitle || "A new chart is ready for review",
      tag: `in-review-${chartId}`,
      data: { chartId },
      requireInteraction: true,
      autoCloseMs: 30000, // Auto-close after 30 seconds
    })

    // Play audio chime
    if (playSound) {
      await this.playChime()
    }
  }

  /**
   * Show notification for multiple new "In Review" charts
   * @param count Number of new charts
   * @param playSound Whether to play audio chime
   */
  async notifyMultipleNewInReviewCharts(
    count: number,
    playSound = true
  ): Promise<void> {
    await this.show({
      title: "Charts Ready for Review",
      body: `${count} new chart${count > 1 ? "s are" : " is"} ready for review`,
      tag: "in-review-multiple",
      requireInteraction: true,
      autoCloseMs: 30000,
    })

    if (playSound) {
      await this.playChime()
    }
  }

  /**
   * Initialize audio context with user gesture
   * Call this on a user interaction (e.g., button click)
   */
  async initializeAudioContext(): Promise<boolean> {
    try {
      if (!this.audioContext) {
        const AudioContextClass =
          window.AudioContext ||
          (window as typeof window & { webkitAudioContext: typeof AudioContext })
            .webkitAudioContext
        this.audioContext = new AudioContextClass()
      }

      if (this.audioContext.state === "suspended") {
        await this.audioContext.resume()
      }

      return true
    } catch (error) {
      console.error("Failed to initialize audio context:", error)
      return false
    }
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    this.closeAll()
    if (this.audioContext) {
      this.audioContext.close()
      this.audioContext = null
    }
  }
}
